import { UploadedFile } from '../../core/types';
import { ReactComponent as PDFIcon } from '../../icons/pdf.svg';
import { ReactComponent as XLSIcon } from '../../icons/xls.svg';
import { ReactComponent as XLSErrIcon } from '../../icons/xls-error.svg';
import { ReactComponent as CSVIcon } from '../../icons/csv.svg';
import { ReactComponent as CSVErrIcon } from '../../icons/csv-error.svg';
import { ReactComponent as TipIcon } from '../../icons/tip-green-circle.svg';
import { ReactComponent as ErrorIcon } from '../../icons/file-error.svg';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import Collapse from '../Collapse/Collapse';
import { ManualUploadFileStatusEnum } from '../../context/useManualUpload';
import MiniLoader from '../MiniLoader/MiniLoader';
import './ManualUploadFileLinkV2.scss';
import { downloadFile } from '../../core/helpers';
import { getS3Link } from '../../core/s3';
import { addNotification } from '../../core/store/notificationsSlice';
import { DEFAULT_ERROR } from '../Notifications/Notification';
import { useAppDispatch } from '../../hooks';
import Tooltip from '../Tooltip/Tooltip';
import Button from '../Button';

interface Props {
  file: UploadedFile;
  collapsed?: boolean;
}

export const ErrorFileIconsMap: {
  [key: string]: JSX.Element;
} = {
  pdf: <PDFIcon />,
  xls: <XLSErrIcon />,
  xlsx: <XLSErrIcon />,
  csv: <CSVErrIcon />,
};

export const FileIconsMap: {
  [key: string]: JSX.Element;
} = {
  pdf: <PDFIcon />,
  xls: <XLSIcon />,
  xlsx: <XLSIcon />,
  csv: <CSVIcon />,
};

export default function ManualUploadFileLinkV2({
  file,
  collapsed = true,
}: Props) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const hasErrors = !!file.errors?.length;

  const modifiedName = file.name
    .split('.')
    .map((v, i, self) => {
      if (i === self.length - 2) {
        return v.replace(/_\d+$/, '..');
      }
      return v;
    })
    .join('.');

  const handleDownload = async () => {
    const parts = `${file.bucket}/${file.path}`.split('/');

    try {
      const url = await getS3Link(parts[0], parts.slice(1).join('/'));
      downloadFile(url, parts[parts.length - 1]);
    } catch (error) {
      dispatch(
        addNotification({
          text: t(DEFAULT_ERROR),
          type: 'error',
        })
      );
    }
  };

  const errorsBlock = (
    <>
      <div className='Text--orange fw-600'>{t('File not accepted')}</div>
      {(file.errors || []).map((err, i) => (
        <div className='mb-1' key={i}>
          {err}
        </div>
      ))}
    </>
  );

  if (hasErrors) {
    return (
      <div className='border-top py-2'>
        <Collapse
          initialOpened={!collapsed}
          triggerRenderer={(isOpened, toggleCollapse) => (
            <div className='d-flex align-items-start justify-content-between'>
              <div className='ManualUploadFileLinkV2__info'>
                <div className='d-flex'>
                  <div className='ManualUploadFileLinkV2__icon rounded-circle me-2'>
                    {
                      ErrorFileIconsMap[
                        file.name.split('.').slice(-1)[0]!.toLowerCase()
                      ]
                    }
                  </div>
                  <div>
                    <div
                      className={'ManualUploadFileLinkV2__name fw-600'}
                      onClick={handleDownload}
                    >
                      {modifiedName}
                    </div>
                    <div className='d-flex align-items-center Text--sm Text--gray mb-2'>
                      {t('sent on')}&nbsp;
                      {format(new Date(file.imported_at), 'dd.MM.yyyy HH:mm')}
                    </div>
                  </div>
                </div>
                {!isOpened && (
                  <Button
                    styling='naked-blue'
                    className='ps-5 fw-600'
                    onClick={toggleCollapse}
                  >
                    {t('Show details')}
                  </Button>
                )}
              </div>
              <Tooltip content={t('File not accepted')}>
                <div className='pt-1'>
                  <ErrorIcon className='icon-24' />
                </div>
              </Tooltip>
            </div>
          )}
          contentRenderer={(ref, toggleCollapse) => (
            <div ref={ref} className='ps-5'>
              <div className='pb-2 mb-n1'>{errorsBlock}</div>
              <Button
                styling='naked-blue'
                className='fw-600'
                onClick={toggleCollapse}
              >
                {t('Hide details')}
              </Button>
            </div>
          )}
        />
      </div>
    );
  }

  return (
    <div className='border-top py-2'>
      <div className='d-flex align-items-start justify-content-between'>
        <div className='ManualUploadFileLinkV2__info'>
          <div className='d-flex'>
            <div className='ManualUploadFileLinkV2__icon rounded-circle me-2'>
              {FileIconsMap[file.name.split('.').slice(-1)[0]!.toLowerCase()]}
            </div>
            <div>
              <div
                className={'ManualUploadFileLinkV2__name fw-600'}
                onClick={handleDownload}
              >
                {modifiedName}
              </div>
              <div className='d-flex align-items-center Text--sm Text--gray mb-2'>
                {t('sent on')}&nbsp;
                {format(new Date(file.imported_at), 'dd.MM.yyyy HH:mm')}
              </div>
            </div>
          </div>
        </div>
        <div className='pt-1'>
          {file.status === ManualUploadFileStatusEnum.CREATED ? (
            <MiniLoader className='MiniLoader--sm d-flex align-items-center justify-content-center' />
          ) : (
            <Tooltip content={t('File is accepted')}>
              <TipIcon className='icon-24' />
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );
}
